import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Brain, Sparkles, Target } from 'lucide-react';

export const ComparisonSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const comparisons = [
    {
      icon: Brain,
      title: "Développement personnel",
      problem: "Tu t'ajoutes des couches pour \"devenir la meilleure version de toi-même\" (nouvelles compétences, habitudes, pensée positive). Il y a toujours quelque chose à améliorer. Tu n'es jamais arrivé·e.",
      solution: "Dans l'école des Maîtres Dauphins, la démarche est inverse. Tu vas te dépouiller de toutes tes couches et conditionnements pour retrouver ta nature profonde : un état de paix et de perfection."
    },
    {
      icon: Sparkles,
      title: "Hypnose",
      problem: "Avec l'hypnose, tu agis seulement sur ton subconscient.",
      solution: "Avec les fréquences delphiques tu vas agir à tous les niveaux : sur les mémoires enfouies dans ton subconscient mais aussi ton corps physique, émotionnel et spirituel. Pas besoin de connaître où se loge le blocage."
    },
    {
      icon: Target,
      title: "Méditation",
      problem: "La méditation permet d'entrer dans un état de conscience modifié mais ne permet pas une expansion des ondes cérébrales pour accéder à des espaces multidimensionnels.",
      solution: "Dans l'école des Maîtres Dauphins, les fréquences quantiques te permettent de plonger dans ces dimensions et d'accélérer ton évolution spirituelle."
    }
  ];

  return (
    <section ref={ref} className="section-padding bg-background relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-accent/30 to-transparent" />

      <div className="container-luxury relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center max-w-4xl mx-auto mb-16"
        >
          <span className="text-accent font-body text-sm tracking-[0.3em] uppercase mb-4 block">
            Différence clé
          </span>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-foreground leading-tight mb-6">
            En quoi l'approche des maîtres dauphins est 
            <span className="text-gradient-gold"> différente des autres ?</span>
          </h2>
          <p className="text-lg text-muted-foreground font-body">
            Dans l'école des Maîtres Dauphins, tu vas vivre un processus d'alchimie intérieure 
            qui te permettra de transmuter tes ombres en lumière.
          </p>
        </motion.div>

        {/* Comparison Cards */}
        <div className="grid md:grid-cols-3 gap-8">
          {comparisons.map((item, index) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.2 + index * 0.15 }}
              className="group"
            >
              <div className="luxury-card rounded-3xl p-8 h-full flex flex-col">
                <div className="w-14 h-14 rounded-xl bg-accent/10 flex items-center justify-center mb-6 group-hover:bg-accent/20 transition-colors">
                  <item.icon className="w-7 h-7 text-accent" />
                </div>
                
                <h3 className="font-display text-xl text-foreground mb-4">{item.title}</h3>
                
                <div className="space-y-4 flex-grow">
                  <div className="p-4 rounded-xl bg-muted/30 border border-border/30">
                    <p className="text-sm text-muted-foreground font-body leading-relaxed">
                      {item.problem}
                    </p>
                  </div>
                  
                  <div className="p-4 rounded-xl bg-accent/10 border border-accent/20">
                    <p className="text-sm text-foreground font-body leading-relaxed">
                      {item.solution}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Animated GIF */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ delay: 0.6 }}
          className="mt-16 flex justify-center"
        >
          <div className="relative">
            <div className="absolute inset-0 bg-accent/20 blur-3xl rounded-full" />
            <img
              src="https://gillesdelieuze.com/wp-content/uploads/2024/11/GMP_U2F2ZUdIMDE.gif"
              alt="Transformation animée"
              className="relative rounded-2xl max-w-xs border border-accent/20"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ComparisonSection;
